export type DomainStatus =
  | "unset"
  | "pending_verification"
  | "provisioning"
  | "active"
  | "failed";

export interface DnsRecordInstruction {
  record_type: string;
  host: string;
  value: string;
  ttl: number;
}

export interface DomainSetupInstructions {
  acm_validation_records?: DnsRecordInstruction[];
  routing_cname?: DnsRecordInstruction;
  domain_connect_url?: string | null;
  /** @deprecated Legacy shape — prefer acm_validation_records + routing_cname */
  txt_record?: DnsRecordInstruction;
  /** @deprecated Legacy shape — prefer routing_cname */
  cname_record?: DnsRecordInstruction;
}

export interface DomainStatusResponse {
  domain: string;
  domain_status: DomainStatus;
  domain_verified_at: string | null;
  domain_dns_configured_at: string | null;
  domain_activated_at: string | null;
  domain_last_error: string;
  canonical_url?: string;
  url?: string;
  setup_instructions?: DomainSetupInstructions;
  confirmed?: boolean;
}

export interface NormalizedDomainSetupInstructions {
  validationRecords: DnsRecordInstruction[];
  routingCname: DnsRecordInstruction | null;
  domainConnectUrl: string | null;
}

export function normalizeSetupInstructions(
  instructions: DomainSetupInstructions | null | undefined,
): NormalizedDomainSetupInstructions {
  if (!instructions) {
    return { validationRecords: [], routingCname: null, domainConnectUrl: null };
  }

  if (instructions.acm_validation_records?.length) {
    return {
      validationRecords: instructions.acm_validation_records,
      routingCname: instructions.routing_cname ?? null,
      domainConnectUrl: instructions.domain_connect_url ?? null,
    };
  }

  const validationRecords = instructions.txt_record ? [instructions.txt_record] : [];
  const routingCname = instructions.cname_record ?? instructions.routing_cname ?? null;

  return {
    validationRecords,
    routingCname,
    domainConnectUrl: instructions.domain_connect_url ?? null,
  };
}

export function parseDomainApiError(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: string; domain?: string[]; non_field_errors?: string[] } };
    message?: string;
  };
  const data = err.response?.data;
  if (data?.detail) return String(data.detail);
  if (data?.domain?.[0]) return String(data.domain[0]);
  if (data?.non_field_errors?.[0]) return String(data.non_field_errors[0]);
  if (err.message) return err.message;
  return fallback;
}

export function mergeDomainResponseIntoSite(
  site: { set?: (data: Record<string, unknown>) => unknown },
  response: DomainStatusResponse,
): void {
  const patch: Record<string, unknown> = {
    domain: response.domain ?? "",
    domainStatus: response.domain_status ?? "unset",
  };
  if (response.canonical_url != null) {
    patch.canonicalUrl = response.canonical_url;
  }
  if (response.url != null) {
    patch.url = response.url;
  }
  site.set?.(patch);
}
